import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

// Using Maskgroup PNGs for Compare Quality icons as per user request
import mask1 from '../../assets/img/icons/Maskgroup1.png';
import mask2 from '../../assets/img/icons/Maskgroup2.png';
import mask3 from '../../assets/img/icons/Maskgroup3.png';
import mask4 from '../../assets/img/icons/Maskgroup4.png';
import BestChoiceRibbon from '../../assets/img/icons/BestChoiceRibbon.png';
import LogoBrand from '../../assets/img/header/logo.png';

const StarRating = ({ count, total = 5 }) => (
  <span className="stars">
    {[...Array(total)].map((_, i) => (
      <span key={i} style={{ color: i < count ? '#ff8a00' : '#dcdcdc', fontSize: '18px' }}>★</span>
    ))}
  </span>
);

const CustomDropdown = ({ options, selected, onSelect, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="custom-dropdown-container" ref={dropdownRef}>
      <div
        className={`custom-dropdown-trigger ${isOpen ? 'active' : ''} ${selected ? 'has-value' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
      >
        <span>{selected || placeholder}</span>
        <ChevronDown size={18} className={`dropdown-chevron ${isOpen ? 'rotate' : ''}`} />
      </div>
      {isOpen && (
        <div className="custom-dropdown-menu">
          {options.map((opt, idx) => (
            <div
              key={idx}
              className={`custom-dropdown-option ${selected === opt ? 'selected' : ''}`}
              onClick={() => {
                onSelect(opt);
                setIsOpen(false);
              }}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CompareQualitySection = ({ title, subtitle }) => {
  const [bType, setBType] = useState("");
  const [sqftValue, setSqftValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({
    otherTat: "4-5 Days",
    scanTat: "1-2 Days",
    otherAfford: "$0",
    scanAfford: "$0",
    efficiency: "5",
    qualityOther: 3,
    qualityScan: 4
  });

  const buildingOptions = ["Residential", "Commercial", "Industrial", "Institutional"];

  const handleCalculate = () => {
    const val = parseFloat(sqftValue);
    if (isNaN(val) || val <= 0) {
      alert("Please enter a valid number for Sq.Ft.");
      return;
    }
    setLoading(true);
    setTimeout(() => {
      let oTat, sTat, oAff, sAff, eff, qO, qS;
      if (val < 1000) {
        oTat = "3-4 Days"; sTat = "1 Day";
        oAff = `$${Math.round(val * 1.5)}`; sAff = `$${Math.round(val * 0.5)}`; eff = "3"; qO = 3; qS = 5;
      } else if (val >= 1000 && val < 5000) {
        oTat = "7-10 Days"; sTat = "2-3 Days";
        oAff = `$${Math.round(val * 1.3)}`; sAff = `$${Math.round(val * 0.45)}`; eff = "4"; qO = 2; qS = 5;
      } else if (val >= 5000) {
        oTat = "15-25 Days"; sTat = "4-6 Days";
        oAff = `$${Math.round(val * 1.1)}`; sAff = `$${Math.round(val * 0.40)}`; eff = "5"; qO = 2; qS = 5;
      } else {
        oTat = "4-5 Days"; sTat = "1-2 Days";
        oAff = "$0"; sAff = "$0"; eff = "5"; qO = 3; qS = 5;
      }
      setResults({ otherTat: oTat, scanTat: sTat, otherAfford: oAff, scanAfford: sAff, efficiency: eff, qualityOther: qO, qualityScan: qS });
      setLoading(false);
    }, 1000);
  };

  return (
    <section className="compare-quality-section">
      <h2 className="compare-title">
        {title || (
          <>
            <span className="compare-title-main" style={{ display: 'block' }}>
              Scan To BIM Projects DIY Calculator:
            </span>
            <span className="compare-title-sub text-primary" style={{ display: 'block' }}>
              Compare Speed, Quality, And Cost
            </span>
          </>
        )}
      </h2>
      <p className="compare-subtitle">
        {subtitle || "Our team of over 90+ scan to bim dedicated modelers, architects and engineers provides expert Scan-to-BIM and Scan-to-CAD services. We cater specifically to Surveyors, Architects, and Engineers working in the ACS and MEPF fields, ensuring precision and tailored solutions for every project."}
      </p>

      <div className="calculator-card">
        <div className="calc-form-card">
          <div className="calc-header-text">Enter Value To Get Detail</div>
          <div className="calc-form">
            <div style={{ flex: 1 }}>
              <CustomDropdown
                options={buildingOptions}
                selected={bType}
                onSelect={(val) => setBType(val)}
                placeholder="Building Type"
              />
            </div>
            <div className="calc-input-wrapper">
              <input
                type="number"
                className="calc-input"
                placeholder="Sq.Ft."
                value={sqftValue}
                onChange={(e) => setSqftValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCalculate();
                }}
              />
            </div>
            <button className="calc-submit" onClick={handleCalculate} disabled={loading}>
              {loading ? "Calculating..." : "Submit"}
            </button>
          </div>
        </div>

        <div className="comparison-grid" style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.3s' }}>

          <div className="comp-box">
            <h3 className="comp-box-title" style={{ color: '#54b248' }}>
              Other Companies
            </h3>
            <div className="comp-table">
              <div className="comp-row-header">
                <span className="roi-matrix-header">ROI Matrix</span>
                <span className="roi-matrix-header">Attributes</span>
              </div>
              <div className="comp-row">
                <div className="comp-label">
                  <img src={mask1.src || mask1} alt="Icon" width="22" height="22" />
                  Affordability
                </div>
                <div className="comp-value">{results.otherAfford}</div>
              </div>
              <div className="comp-row">
                <div className="comp-label">
                  <img src={mask2.src || mask2} alt="Icon" width="22" height="22" />
                  TAT
                </div>
                <div className="comp-value">{results.otherTat}</div>
              </div>
              <div className="comp-row">
                <div className="comp-label">
                  <img src={mask3.src || mask3} alt="Icon" width="22" height="22" />
                  Quality
                </div>
                <div className="comp-value"><StarRating count={results.qualityOther} /></div>
              </div>
              <div className="comp-row">
                <div className="comp-label">
                  <img src={mask4.src || mask4} alt="Icon" width="22" height="22" />
                  Support
                </div>
                <div className="comp-value">Emails</div>
              </div>
            </div>
          </div>

          <div className="comp-box featured">
            {/* Custom CSS Ribbon Design */}
            <div className="css-ribbon">
              <span>Best</span>
              <span>Choice</span>
            </div>

            <h3 className="comp-box-title">
              <span style={{ color: '#54b248', fontSize: '28px' }}>With</span>
              <img src={LogoBrand.src || LogoBrand} style={{ height: '40px', }} alt="Logo" />
            </h3>
            <div className="comp-table">
              <div className="comp-row-header">
                <span className="roi-matrix-header">ROI Matrix</span>
                <span className="roi-matrix-header">Attributes</span>
              </div>
              <div className="comp-row">
                <div className="comp-label">
                  <img src={mask1.src || mask1} alt="Icon" width="22" height="22" />
                  Affordability
                </div>
                <div className="comp-value">{results.scanAfford}</div>
              </div>
              <div className="comp-row">
                <div className="comp-label">
                  <img src={mask2.src || mask2} alt="Icon" width="22" height="22" />
                  TAT
                </div>
                <div className="comp-value">{results.scanTat}</div>
              </div>
              <div className="comp-row">
                <div className="comp-label">
                  <img src={mask3.src || mask3} alt="Icon" width="22" height="22" />
                  Quality
                </div>
                <div className="comp-value"><StarRating count={results.qualityScan} /></div>
              </div>
              <div className="comp-row">
                <div className="comp-label">
                  <img src={mask4.src || mask4} alt="Icon" width="22" height="22" />
                  Support
                </div>
                <div className="comp-value" style={{ textAlign: 'right', fontWeight: '700' }}>
                  24/7 Chat + Email <br /> + Call
                </div>
              </div>
            </div>
          </div>

        </div>

        <div className="efficiency-banner">
          3 Times More Efficient Than Competitors
        </div>
      </div>
    </section>
  );
};

export default CompareQualitySection;
